import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, CalendarCheck, Info, MapPin } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../Reveal";
import { Overline } from "../Section";
import { HOME_VISIT_BENEFITS, HOME_VISIT_AREAS, CLINIC_PHOTOS, telLink, whatsappLink, CLINIC, HOME_VISIT_WHATSAPP_MSG } from "../../data/site";

export const HomeVisit = () => (
  <section className="relative overflow-hidden bg-dash-bg py-24 md:py-32" data-testid="home-visit-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] shadow-[0_30px_60px_rgb(10,37,64,0.12)]">
            <img src={CLINIC_PHOTOS.homeVisit} alt="Home-visit physiotherapy care" className="h-[380px] lg:h-[520px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-dash-ink/40 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/90 backdrop-blur-xl p-5 border border-white/60">
              <p className="font-display text-lg font-bold text-dash-ink">Care at your doorstep</p>
              <p className="text-sm text-dash-muted mt-1">Home visit physiotherapy in Belpahar &amp; Jharsuguda.</p>
            </div>
          </div>
        </Reveal>

        {/* Service areas */}
        <Reveal delay={0.1}>
          <div className="mt-6 rounded-[2rem] border border-dash-line bg-white p-6" data-testid="home-visit-service-areas">
            <p className="font-display text-lg font-bold text-dash-ink">Home Visit Service Areas</p>
            <div className="mt-3 flex flex-wrap gap-2.5">
              {HOME_VISIT_AREAS.map((a) => (
                <span key={a} data-testid={`service-area-${a.toLowerCase()}`} className="inline-flex items-center gap-1.5 rounded-full bg-dash-teal/10 border border-dash-teal/25 px-4 py-2 text-sm font-semibold text-dash-teal">
                  <MapPin className="h-4 w-4" /> {a}
                </span>
              ))}
            </div>
            <p className="mt-3 text-sm text-dash-muted leading-relaxed">
              Home visit physiotherapy services are primarily available in Belpahar and the Jharsuguda area.
              Please contact us to confirm availability at your location.
            </p>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        <Overline>Home Visit Physiotherapy</Overline>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold text-dash-ink leading-tight">Why Choose Home Visit Physiotherapy?</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-4 text-lg font-medium text-dash-teal">Professional physiotherapy care, delivered at your doorstep.</p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 text-dash-muted leading-relaxed max-w-2xl">
            Professional physiotherapy doesn't always require a clinic visit. DASH Physiotherapy Clinic provides
            home visit physiotherapy for patients who prefer or require treatment at home — primarily in Belpahar
            and the Jharsuguda area.
          </p>
        </Reveal>

        <Stagger className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {HOME_VISIT_BENEFITS.map((s) => {
            const Icon = Icons[s.icon] || Icons.Home;
            return (
              <StaggerItem key={s.name}>
                <div data-testid={`home-visit-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-3 rounded-2xl border border-dash-line bg-white px-4 py-3.5 transition-colors duration-300 hover:border-dash-teal">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-dash-teal/10 text-dash-teal"><Icon className="h-5 w-5" /></span>
                  <span className="text-sm font-medium text-dash-ink">{s.name}</span>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>

        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link to="/contact?service=Home%20Visit%20Physiotherapy" data-testid="home-visit-book" className="inline-flex items-center justify-center gap-2 rounded-full bg-dash-teal px-6 py-3.5 font-semibold text-white transition-all hover:bg-dash-ink hover:-translate-y-0.5">
              <CalendarCheck className="h-5 w-5" /> Schedule a Home Visit
            </Link>
            <a href={telLink} data-testid="home-visit-call" className="inline-flex items-center justify-center gap-2 rounded-full border border-dash-line bg-white px-6 py-3.5 font-semibold text-dash-ink transition-all hover:border-dash-teal hover:-translate-y-0.5">
              <Phone className="h-5 w-5 text-dash-teal" /> Call {CLINIC.phone}
            </a>
            <a href={whatsappLink(HOME_VISIT_WHATSAPP_MSG)} target="_blank" rel="noreferrer" data-testid="home-visit-whatsapp" className="inline-flex items-center justify-center gap-2 rounded-full border border-dash-line bg-white px-6 py-3.5 font-semibold text-dash-ink transition-all hover:border-[#25D366] hover:-translate-y-0.5">
              <MessageCircle className="h-5 w-5 text-[#25D366]" /> WhatsApp Us
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-6 flex items-start gap-2 text-sm text-dash-muted">
            <Info className="h-4 w-4 shrink-0 text-dash-gold mt-0.5" />
            Home visits are subject to availability and service area. Please contact the clinic to confirm.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
